import { Button, showDialog } from '@bpinternal/ui-kit-next'
import { Link } from '@tanstack/react-router'
import { Database, FolderOpen, HardDrive, Plus } from 'lucide-react'
import { Boundary } from '~/components'
import { EmptyState } from '~/elements/EmptyState'
import { useListKnowledgeBase } from '~/features/knowledge-bases/hooks/useKnowledgeBase'
import { ProgressBarCard } from '~/features/usage/components'
import { formatValueWithUnit } from '~/features/usage/helpers'
import { useInfiniteScroll, useUsage } from '~/hooks'
import { formatters } from '~/utils'
import { PreventKbUse } from '../PreventKbUse'
import { KnowledgeBaseSummary } from '../types'
import { AddKBDialog } from './AddKBDialog'
import { KnowledgeBaseCard } from './KnowledgeBaseCard'

type Props = {
  workspaceId: string
  botId: string
  legacyKbs: { id: string; name: string }[]
}

export const KnowledgeBases = (props: Props) => {
  const { botId, legacyKbs } = props
  if (legacyKbs.length > 0) {
    return <PreventKbUse kbs={legacyKbs} open={legacyKbs.length > 0} botId={botId} />
  }

  return (
    <div className="flex flex-col gap-14 md:gap-8">
      <Boundary>
        <KBUsages {...props} />
        <KnowledgeBasesGrid {...props} />
      </Boundary>
    </div>
  )
}

const KBUsages = (props: Props) => {
  const { workspaceId, legacyKbs } = props
  const currentPeriod = new Date().toISOString().split('T')[0]

  const { storage_count: fileStorage, knowledgebase_vector_storage: vectorStorage } = useUsage({
    workspaceId,
    quotas: ['storage_count', 'knowledgebase_vector_storage'],
    period: currentPeriod,
  })

  if (legacyKbs.length > 0) {
    return null
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <ProgressBarCard
        className="bg-card-filled border-none shadow-none dark:bg-neutral-900"
        title={
          <div className="mb-2 flex flex-row items-center justify-between gap-2">
            <div className="flex items-center gap-4">
              <HardDrive />
              <p>File Storage</p>
            </div>
            <Link to={'/workspaces/$workspaceId/billing/add-ons'} params={{ workspaceId }}>
              <Button variant="outline" className="hidden bg-gray-1 shadow-none md:block">
                Add Storage
              </Button>
            </Link>
          </div>
        }
        footerClassName="hidden md:block"
        footer={<p className="text-sm text-muted-foreground">Usage includes uploaded files and generated artifacts.</p>}
        value={fileStorage.percentage}
      >
        <div className="mb-3 flex justify-between text-sm text-muted-foreground">
          <p>
            {formatValueWithUnit(fileStorage.value, 'storage_count')} /{' '}
            {formatValueWithUnit(fileStorage.quota, 'storage_count')}
          </p>
          <p>{formatters.number(fileStorage.percentage, 0)}%</p>
        </div>
      </ProgressBarCard>
      <ProgressBarCard
        className="bg-card-filled border-none shadow-none dark:bg-neutral-900"
        title={
          <div className="mb-2 flex flex-row items-center justify-between gap-2">
            <div className="flex items-center gap-4">
              <Database />
              <p>Vector Storage</p>
            </div>
            <Link to={'/workspaces/$workspaceId/billing/add-ons'} params={{ workspaceId }}>
              <Button variant="outline" className="hidden bg-gray-1 shadow-none md:block">
                Add Storage
              </Button>
            </Link>
          </div>
        }
        value={vectorStorage.percentage}
        footerClassName="hidden md:block"
        footer={<p className="text-sm text-muted-foreground">Stores document parts and meanings in your vector DB.</p>}
      >
        <div className="mb-3 flex justify-between text-sm text-muted-foreground">
          <p>
            {formatValueWithUnit(vectorStorage.value, 'knowledgebase_vector_storage')} /{' '}
            {formatValueWithUnit(vectorStorage.quota, 'knowledgebase_vector_storage')}
          </p>
          <p>{formatters.number(vectorStorage.percentage, 0)}%</p>
        </div>
      </ProgressBarCard>
    </div>
  )
}

const KnowledgeBasesGrid = (props: Props) => {
  const { workspaceId, botId } = props
  const { data: knowledgeBases, hasNextPage, fetchNextPage } = useListKnowledgeBase({ workspaceId, botId })

  const bottomRef = useInfiniteScroll(fetchNextPage, hasNextPage)

  if (knowledgeBases.length === 0) {
    return (
      <div className="mx-auto my-auto max-w-4xl lg:my-20">
        <EmptyState
          className="mx-auto max-w-[var(--container-2)]"
          icon={FolderOpen}
          title={'You have no knowledge bases'}
          description={'Start building your knowledge bases'}
          primaryAction={
            <Button
              variant="ghost"
              onClick={() =>
                showDialog(({ close }) => <AddKBDialog workspaceId={workspaceId} botId={botId} close={close} />, {
                  title: 'Create a Knowledge Base',
                })
              }
            >
              <Plus />
              Create Knowledge Base
            </Button>
          }
        />
      </div>
    )
  }

  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(320px,1fr))] justify-items-center gap-6 2xl:grid-cols-3">
      {knowledgeBases.map((kb: KnowledgeBaseSummary) => (
        <KnowledgeBaseCard key={kb.id} {...kb} {...props} />
      ))}
      <div ref={bottomRef} />
    </div>
  )
}
